import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Phone, MessageCircle, Instagram, MapPin, Clock, Mail, Send, CheckCircle2 } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import api from "@/lib/api";
import { toast } from "sonner";

const Contact = () => {
    const { settings } = useSettings();
    const location = useLocation();
    const presetService = location.state?.service || "";
    const [form, setForm] = useState({
        name: "", phone: "", email: "",
        event_type: presetService, message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    if (!settings) return null;
    const phone = settings.phone;
    const wa = (settings.whatsapp || phone).replace(/[^\d]/g, "");

    const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.phone || !form.message) {
            toast.error("Please fill name, phone and message");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/enquiries", form);
            setDone(true);
            toast.success("Enquiry sent! Our team will reach out shortly.");
            setForm({ name: "", phone: "", email: "", event_type: "", message: "" });
        } catch {
            toast.error("Failed to send. Please WhatsApp us instead.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page" className="pt-32 pb-24 bg-bg-base">
            <section className="px-6 md:px-12 mb-16">
                <div className="max-w-7xl mx-auto text-center">
                    <div className="text-xs uppercase tracking-[0.4em] text-gold mb-5">Contact Us</div>
                    <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white leading-tight">
                        Let's plan your <span className="italic gold-text-gradient">moment</span>
                    </h1>
                    <p className="mt-6 text-white/70 max-w-2xl mx-auto">Talk to our event specialists — quotes within hours, not days.</p>
                </div>
            </section>

            {/* Quick contact cards */}
            <section className="px-6 md:px-12 mb-16">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
                    <a href={`tel:${phone}`} data-testid="quick-call" className="glass-card rounded-xl p-7 hover:border-gold/50 transition-all hover:-translate-y-1 block">
                        <Phone className="text-gold mb-4" size={28} />
                        <div className="text-xs uppercase tracking-[0.2em] text-white/60 mb-1">Call</div>
                        <div className="font-serif text-xl text-white">{phone}</div>
                    </a>
                    <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" data-testid="quick-whatsapp" className="glass-card rounded-xl p-7 hover:border-gold/50 transition-all hover:-translate-y-1 block">
                        <MessageCircle className="text-gold mb-4" size={28} />
                        <div className="text-xs uppercase tracking-[0.2em] text-white/60 mb-1">WhatsApp</div>
                        <div className="font-serif text-xl text-white">Chat with us</div>
                    </a>
                    <a href={settings.instagram_url} target="_blank" rel="noreferrer" data-testid="quick-instagram" className="glass-card rounded-xl p-7 hover:border-gold/50 transition-all hover:-translate-y-1 block">
                        <Instagram className="text-gold mb-4" size={28} />
                        <div className="text-xs uppercase tracking-[0.2em] text-white/60 mb-1">Instagram</div>
                        <div className="font-serif text-xl text-white">@stareventnagpur</div>
                    </a>
                </div>
            </section>

            {/* Form + Map */}
            <section className="px-6 md:px-12 mb-20">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10">
                    {/* Form */}
                    <div className="glass-card rounded-2xl p-7 md:p-10">
                        <h2 className="font-serif text-2xl md:text-3xl text-white mb-6">Send us an enquiry</h2>
                        {done ? (
                            <div className="text-center py-8" data-testid="enquiry-success">
                                <CheckCircle2 className="text-gold mx-auto mb-4" size={48} />
                                <p className="text-white/85 mb-4">Thanks! We'll be in touch within a few hours.</p>
                                <button onClick={() => setDone(false)} className="btn-outline-gold text-sm">Send another</button>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                                <input data-testid="contact-name" value={form.name} onChange={onChange("name")} placeholder="Your name *" className="w-full bg-bg-base border border-white/15 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-gold focus:outline-none transition-colors" />
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <input data-testid="contact-phone" value={form.phone} onChange={onChange("phone")} placeholder="Phone *" className="bg-bg-base border border-white/15 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-gold focus:outline-none transition-colors" />
                                    <input data-testid="contact-email" value={form.email} onChange={onChange("email")} placeholder="Email (optional)" className="bg-bg-base border border-white/15 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-gold focus:outline-none transition-colors" />
                                </div>
                                <input data-testid="contact-event-type" value={form.event_type} onChange={onChange("event_type")} placeholder="Event type (Wedding, Corporate, Birthday…)" className="w-full bg-bg-base border border-white/15 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-gold focus:outline-none transition-colors" />
                                <textarea data-testid="contact-message" value={form.message} onChange={onChange("message")} placeholder="Tell us about your event… *" rows={5} className="w-full bg-bg-base border border-white/15 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-gold focus:outline-none transition-colors resize-none" />
                                <button type="submit" disabled={submitting} data-testid="contact-submit" className="btn-gold w-full inline-flex items-center justify-center gap-2">
                                    {submitting ? "Sending..." : <>Send Enquiry <Send size={16} /></>}
                                </button>
                            </form>
                        )}
                    </div>

                    {/* Details + Map */}
                    <div className="space-y-6">
                        <div className="glass-card rounded-2xl p-7">
                            <h3 className="font-serif text-xl text-white mb-5">Studio Details</h3>
                            <ul className="space-y-4 text-sm">
                                <li className="flex gap-3"><MapPin size={18} className="text-gold mt-0.5 shrink-0" /><span className="text-white/80">{settings.address}</span></li>
                                <li className="flex gap-3"><Phone size={18} className="text-gold mt-0.5 shrink-0" /><a href={`tel:${phone}`} className="text-white/80 hover:text-gold">{phone}</a></li>
                                <li className="flex gap-3"><Clock size={18} className="text-gold mt-0.5 shrink-0" /><span className="text-white/80">{settings.business_hours}</span></li>
                            </ul>
                        </div>
                        <div className="rounded-2xl overflow-hidden border border-white/10 min-h-[280px]">
                            <iframe
                                data-testid="contact-map"
                                title="Star Events Map"
                                src={settings.google_maps_embed}
                                className="w-full h-[320px]"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;
